import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
  multiFactor,
  updatePassword,
  SAMLAuthProvider,
  OAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import MyStorage from './storage';
import { getFirebaseClientConfig, getRuntimeConfig } from './runtimeConfig';
import { applyFirebaseTenant } from '../firebase_runtime';

const runtimeConfig = getRuntimeConfig();
initializeApp(getFirebaseClientConfig());

// Identity Platform tenant selection is request-time state, not a build
// decision. Set it before creating providers or starting password, redirect,
// MFA, reset, or enrollment-related authentication.
const auth = getAuth();
applyFirebaseTenant(auth, runtimeConfig.firebase.tenantId);
const phoneAuthProvider = new PhoneAuthProvider(auth);

export const MFA_PHONE_FACTORID = PhoneMultiFactorGenerator.FACTOR_ID;
export const getUser = () => {
  auth.currentUser?.getIdToken(true);
  return auth.currentUser;
};

/** Clears Firebase client auth state only — does not end the corporate IdP session. */
export const signOutOfFirebase = async () => {
  await signOut(auth);
};

export const getRecaptcha = (recaptchaEl) => {
  return new RecaptchaVerifier(auth, recaptchaEl, {size: "invisible"});
}

export const updateFirebasePassword = async (password) => {
  let user = getUser();
  if (!user) throw {code: 'custom/no-user-found'}
  return await updatePassword(user, password)
    .then(() => true)
    .catch(() => false);
}

export const updateMostRecentFactors = (factors) => {
  const factorObj = {};
  if (!Array.isArray(factors)) return;
  factors.forEach(factor => {
    if (!factor.factorId) return;
    if (!factorObj[factor.factorId]) factorObj[factor.factorId] = factor;
    else {
      const [date1, date2] = [
        new Date(factorObj[factor.factorId].enrollmentTime || 0),
        new Date(factor.enrollmentTime || 0)
      ];
      if(date2.getTime() > date1.getTime()) factor[factor.factorId] = factor;
    }
  });
  MyStorage.set('factors', factorObj);
}

// Firebase error codes that mean "auth is temporarily unavailable" rather
// than "the credentials are wrong" — e.g. Firebase throttling repeated
// sign-ins from the same IP (auth/too-many-requests), or transient network/
// backend problems. These must NOT be treated as a failed password.
const TRANSIENT_FIREBASE_ERROR_CODES = new Set([
  'auth/too-many-requests',
  'auth/network-request-failed',
  'auth/internal-error',
  'auth/timeout'
]);

export const signInToFirebase = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password)
    .then(credential => {
      return {
        type: 'credential',
        data: credential
      }})
    .catch(err => {
      if (!err?.code) console.error(err);
      if (err.code == 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);
        updateMostRecentFactors(resolver?.hints || []);
        return {
          type: 'multiFactor',
          data: resolver
        }
      }
      // Surface transient failures distinctly so the caller can ask the user
      // to retry instead of silently falling back to a raw password attempt
      // (which fails as "incorrect password" for Firebase-managed accounts).
      if (TRANSIENT_FIREBASE_ERROR_CODES.has(err?.code)) {
        return {
          type: 'transientError',
          code: err.code
        }
      }
      return null;
    });
  return result;
}

const credentialWithToken = async (credential) => {
  if (!credential?.user) return null;
  return {
    type: 'credential',
    data: {
      user: {
        accessToken: await credential.user.getIdToken(),
        email: credential.user.email || null
      }
    }
  };
};

const isSsoProviderNotFound = (error) => [
  'auth/invalid-provider-id',
  'auth/provider-not-found',
  'auth/operation-not-allowed'
].includes(error?.code);

/**
 * Firebase provider class is dictated by the provider-id prefix: per-org
 * SAML registrations are `saml.<slug>`; per-org (and staff) OIDC
 * registrations are `oidc.<slug>` and sign in through the generic
 * OAuthProvider. Alpha owns which id an email routes to.
 */
export const createFederatedProvider = (providerId) => {
  const id = String(providerId || '');
  if (id.startsWith('saml.')) return new SAMLAuthProvider(id);
  if (id.startsWith('oidc.')) return new OAuthProvider(id);
  throw new Error(`Unsupported SSO provider id: ${id}`);
};

const signInWithSsoPopup = async (provider) => {
  try {
    const credential = await signInWithPopup(auth, provider);
    return credentialWithToken(credential);
  } catch (popupError) {
    if (isSsoProviderNotFound(popupError)) return { type: 'providerNotFound' };
    if (popupError?.code === 'auth/popup-blocked') return { type: 'popupBlocked' };
    throw popupError;
  }
};

export const signInWithSso = async (providerId) => {
  if (!runtimeConfig.capabilities.sso) {
    return { type: 'providerNotFound' };
  }
  let provider;
  try {
    provider = createFederatedProvider(providerId);
  } catch (err) {
    return { type: 'providerNotFound' };
  }

  // HTTP localhost cannot use the same-origin redirect proxy reliably.
  if (runtimeConfig.firebase.usePopupForSso) {
    return signInWithSsoPopup(provider);
  }

  try {
    await signInWithRedirect(auth, provider);
    return { type: 'redirect' };
  } catch (redirectError) {
    if (isSsoProviderNotFound(redirectError)) return { type: 'providerNotFound' };
    console.warn('SSO redirect sign-in failed; attempting popup fallback.', redirectError);
    return signInWithSsoPopup(provider);
  }
};

export const getSsoRedirectResult = async () => {
  try {
    const credential = await getRedirectResult(auth);
    return credentialWithToken(credential);
  } catch (error) {
    if (isSsoProviderNotFound(error)) return { type: 'providerNotFound' };
    throw error;
  }
};

// Legacy names — SAML-era imports still used by not-yet-migrated bundles.
export const signInWithSaml = signInWithSso;
export const getSamlRedirectResult = getSsoRedirectResult;

export const multiFactorAuth = async (session, factor, recaptchaVerifier) => {
  switch(factor.factorId) {
    case PhoneMultiFactorGenerator.FACTOR_ID:
      const phoneInfoOptions = {
        multiFactorHint: factor,
        session
      }
      return verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
    default:
      break;
  }
}

export const verifyPhoneNumber = async (options, recaptchaVerifier) => {
  const verifiedId = await phoneAuthProvider.verifyPhoneNumber(options, recaptchaVerifier);
  return verifiedId;
}

export const getUserSession = async () => {
  const user = getUser();
  if (!user) throw ({code: 'custom/user-not-found'});
  return await multiFactor(user).getSession();
}

export const getFactorType = (factor) => {
  if (!factor) return "unknown";
  switch(factor.factorId) {
    case PhoneMultiFactorGenerator.FACTOR_ID:
      return "phone";
    default:
      return "unknown";
  }
}

export const getPhoneAssertion = (verificationId, code) => {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  return PhoneMultiFactorGenerator.assertion(cred);
}

export const resolveVerification = async (type, verificationId, code, resolver) => {
  let assertion;
  switch(type) {
    case "phone":
      assertion = getPhoneAssertion(verificationId, code);
      break;
    default:
      console.error("Unknown factor type");
      return null;
  }
  return await resolver.resolveSignIn(assertion);
}

export const enrollPhoneNumber = async(verificationId, code) => {
  const assertion = getPhoneAssertion(verificationId, code)
  await multiFactor(getUser()).enroll(assertion, "Default");
  return;
}
